import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from '../../../app.service';
import { ApiRequestService } from '../../../shared/api-request.service';
import { UtilsService } from '../../../shared/utils.service';
import { CustomDataSource } from '../../../utils/custom-data-source';
import { FolderModel } from 'src/app/shared/folder.model';
import { FolderService } from 'src/app/shared/folder.service';
import { MatDialog } from '@angular/material/dialog';
import { AccountDocsLayoutComponent } from '../account-docs-layout/account-docs-layout.component';
import { TemplateDocsViewComponent } from '../../../template-docs/template-docs-view/template-docs-view.component';
import { RBACService } from '../../../shared/rbac.service';

@Component({
  selector: 'app-account-docs',
  templateUrl: './account-docs.component.html',
  styleUrls: ['./account-docs.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountDocsComponent implements OnInit, AfterViewInit {
  // columns to show and the data source
  displayedColumns: string[] = [
    'select',
    'name',
    'folder_type',
    'visibility',
    'expiry',
    'created_at',
    'actions'
  ];
  dataSource = new MyDocsDataSource(this.app, this.api);

  // Permission flags resolved ONCE in ngOnInit() so the template does not
  // re-evaluate rbac.userCanAccess() on every change detection cycle.
  canCreate: boolean = false;
  canView: boolean = false;
  canViewAndEdit: boolean = false;
  canViewAndDelete: boolean = false;

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private folderService: FolderService,
    private dialog: MatDialog,
    public rbac: RBACService
  ) {}

  // Expose RBACService to template
  protected readonly RBACService = RBACService;

  ngOnInit() {
    // Resolve the permission flags first so they are always set, even when the early return below fires.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );

    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );

    this.canViewAndEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );

    this.canViewAndDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // Check if user has ANY permission for account documents (view OR create OR edit OR delete)
    const hasAnyPermission = this.canView || this.canCreate; // OR logic: user needs ANY of these permissions

    if (!hasAnyPermission) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access company documents.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    this.dataSource.account_id = this.app.account.id;

    // Only load data if user has view permission
    if (this.canView) {
      this.dataSource.getData();
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without view permission Angular never creates those view children and
    // this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === 'expiry_UTC') {
        this.dataSource.sort_by = 'expiry';
      } else {
        this.dataSource.sort_by = this.sort.active;
      }

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onAdd() {
    // Check create permission
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create company documents.'
      );
      return;
    }

    this.openDialog({ account_id: this.app.account.id } as FolderModel);
  }

  onEdit(folder: FolderModel) {
    // Check edit permission (requires view + edit)
    if (!this.canViewAndEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit company documents.'
      );
      return;
    }

    this.openDialog(folder);
  }

  private openDialog(folder: FolderModel) {
    // Pass permissions to shared component
    this.dialog
      .open(AccountDocsLayoutComponent, {
        width: '1200px',
        data: {
          folder: folder,
          is_training_doc: true,
          show_document_types_list_for: 'account',
          show_document_visibility: true,
          // Reuse the flags resolved once in ngOnInit() rather than re-evaluating the same
          // permission lookups on every dialog open.
          permissions: {
            feature: RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS,
            canCreate: this.canCreate,
            canEdit: this.canViewAndEdit,
            canDelete: this.canViewAndDelete
          }
        }
      })
      .afterClosed()
      .subscribe((result) => {
        // Refresh the list however the dialog is closed, but only when the user can view it. A
        // create-only user has no list to refresh, and the list endpoint requires view permission.
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  onView(folder: FolderModel) {
    // Layer 2 guard: the row button is inside the view-gated table, but the method can also be
    // called programmatically, so it enforces the permission itself.
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view company documents.'
      );
      return;
    }

    this.dialog
      .open(TemplateDocsViewComponent, {
        width: '750px',
        data: {
          folder: folder,
          is_training_doc: true,
          permissions: {
            feature: RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS,
            canCreate: false,
            canEdit: false,
            canDelete: false
          }
        }
      })
      .afterClosed()
      .subscribe((result) => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData(true);
      });
  }

  onRemove(id: number) {
    // Check delete permission
    if (!this.canViewAndDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete company documents.'
      );
      return;
    }

    this.utils.showModal(
      'Remove Document',
      'Are you sure you want to remove this document?',
      () => {
        this.folderService.delete([id]).subscribe(() => {
          this.utils.showToast('Deleted a Folder');
          this.dataSource.getData(true);
        });
      }
    );
  }

  onDeleteSelected() {
    // Check delete permission
    if (!this.canViewAndDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete company documents.'
      );
      return;
    }

    this.utils.showModal(
      'Remove Selected Documents',
      'Are you sure you want to remove the selected documents?',
      () => {
        this.deleteFolders(this.dataSource.selection.selected);
      }
    );
  }

  private deleteFolders(folderIds: number[]) {
    this.folderService.deleteMany(folderIds).subscribe(() => {
      this.utils.showToast('The selected documents were removed.');
      this.dataSource.selection.clear();
      this.dataSource.getData(true);
    });
  }
}

/**
 * the data source class should contain everything needed to get data for the feature.
 * this only applies to lists. forms will use direct calls to the api.
 */
export class MyDocsDataSource extends CustomDataSource {
  account_id = 0;

  // record sorting and direction
  sort_by = 'name';
  sort_order = 'asc';

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI(
      'folders/account/' + this.account_id,
      resetOffset,
      () => {},
      { eager_load: true }
    );
  }
}
